import React, { useState, useRef, useEffect } from 'react';
import { ZoomIn, ZoomOut, Download, Maximize } from 'lucide-react';

const ImageViewer = ({ src, alt, fileName }) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);

    const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 4));
    const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
    const handleReset = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = src;
        link.download = fileName || 'download-image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleMouseDown = (e) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    // Wheel to zoom
    const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (e.deltaY < 0) {
                handleZoomIn();
            } else {
                handleZoomOut();
            }
        }
    };

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('wheel', handleWheel, { passive: false });
            return () => container.removeEventListener('wheel', handleWheel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center bg-gray-100 overflow-hidden group rounded-lg"
        >
            {/* Controls Container */}
            <div className="absolute top-4 right-4 z-10 flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 backdrop-blur pb-1 pt-1 px-2 rounded-lg shadow-sm border border-gray-200">
                <button
                    onClick={handleZoomIn}
                    className="p-1.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    title="Zoom In"
                >
                    <ZoomIn size={18} />
                </button>
                <button
                    onClick={handleZoomOut}
                    className="p-1.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    title="Zoom Out"
                >
                    <ZoomOut size={18} />
                </button>
                <button
                    onClick={handleReset}
                    className="p-1.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    title="Reset View"
                >
                    <Maximize size={18} />
                </button>
                <div className="hidden md:block w-px bg-gray-300 my-1.5 mx-1"></div>
                <div className="md:hidden h-px bg-gray-300 mx-1.5 my-1"></div>
                <button
                    onClick={handleDownload}
                    className="p-1.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    title="Download"
                >
                    <Download size={18} />
                </button>
            </div>

            <div
                className={`w-full h-full flex items-center justify-center ${scale > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                <img
                    src={src}
                    alt={alt || "Image preview"}
                    className="max-w-full max-h-full object-contain pointer-events-none transition-transform duration-100 ease-out"
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                        transformOrigin: 'center'
                    }}
                />
            </div>
        </div>
    );
};

export default ImageViewer;
